import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { Star, Heart, Volume2, Lightbulb, Cloud, ArrowRight, Play, X } from "lucide-react";
import api from "../lib/api";
import { Reveal, MaskedLines, Marquee, EASE } from "../components/motion";
import Starfield from "../components/Starfield";
import FindBuddySection from "../components/FindBuddySection";
import { HomeStorySections } from "../components/HomeSections";




function VideoModal({ open, onClose }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-night/85 p-5 backdrop-blur-sm"
          data-testid="video-modal" onClick={onClose}>
          <motion.div initial={{ opacity: 0, scale: 0.92, y: 24 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 12 }}
            transition={{ duration: 0.35, ease: EASE }} className="relative w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={onClose} aria-label="Close video" data-testid="video-modal-close"
              className="absolute -top-12 right-0 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/25">
              <X size={20} />
            </button>
            <video src="/assets/hero-video.mp4" controls autoPlay muted playsInline loop preload="metadata"
              className="w-full rounded-[1.75rem] shadow-[0_30px_90px_rgba(0,0,0,0.5)]" data-testid="hero-video" />
            <p className="mt-4 text-center font-script text-2xl font-semibold text-white/85">Big hugs. Brighter nights. ♡</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <section ref={ref} className="relative overflow-hidden" data-testid="hero-section">
      {/* Desktop hero */}
      <div className="relative hidden lg:block">
        <motion.div style={{ y }} className="absolute inset-0">
          <img src="/assets/hero.png" alt="Child sleeping peacefully while cuddling a glowing Bedtime Buddy in a softly lit bedroom"
            className="h-full w-full scale-105 object-cover object-center" data-testid="hero-image" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-night/85 via-navy/45 to-transparent" aria-hidden="true" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-night/60 to-transparent" aria-hidden="true" />
        <Starfield count={30} />
        <motion.img src="/assets/badge-smart-plushie.webp" alt="The Original Smart Plushie badge"
          initial={{ opacity: 0, scale: 0.6, rotate: -8 }} animate={{ opacity: 1, scale: 1, rotate: 6 }}
          transition={{ delay: 0.9, duration: 0.6, type: "spring", bounce: 0.45 }}
          className="absolute bottom-[20%] right-[7%] z-10 w-44 drop-shadow-2xl xl:w-52"
          data-testid="hero-note-right-desktop" />
        <div className="relative mx-auto flex min-h-[88vh] max-w-7xl items-center px-6 pb-40 pt-16">
          <div className="max-w-2xl">
            <motion.img src="/assets/logo.png" alt="Bedtime Buddies logo" data-testid="hero-logo"
              initial={{ opacity: 0, y: 20, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.8, ease: EASE }}
              className="h-28 w-auto drop-shadow-2xl" />
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
              className="mt-6 text-sm font-bold uppercase tracking-[0.3em] text-moon">
              Big Hugs. Brighter Nights.™
            </motion.p>
            <h1 className="mt-6 font-display text-4xl font-medium leading-[1.06] tracking-tight text-white drop-shadow-lg sm:text-5xl lg:text-[4.4rem]" data-testid="hero-headline">
              <MaskedLines lines={["Bedtime feels", "different when", <>you're <span className="text-pink">not alone.</span> <Heart className="mb-2 inline h-10 w-10 text-pink" aria-hidden="true" /></>]} />
            </h1>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 0.55 }}>
              <p className="mt-7 max-w-xl text-lg leading-relaxed text-white/90 drop-shadow">
                Oversized, softly weighted companions that comfort, help with self-soothing, and bring a little more magic to bedtime.
              </p>
              <div className="mt-9 flex flex-wrap gap-4">
                <Link to="/buddy/luna-the-unicorn" data-testid="hero-meet-buddies-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-moon px-10 py-5 font-body text-lg font-bold leading-none text-navy shadow-xl transition hover:scale-[1.04] hover:bg-pink">
                  Find Their Buddy <ArrowRight size={19} />
                </Link>
                <button type="button" onClick={() => setVideoOpen(true)} data-testid="hero-watch-video-cta-desktop"
                  className="inline-flex items-center gap-2.5 rounded-full border border-white/40 bg-white/10 px-10 py-5 font-body text-lg font-semibold leading-none text-white backdrop-blur-sm transition hover:border-moon hover:text-moon">
                  <Play size={18} className="fill-current" /> Watch the Video
                </button>
              </div>
              <p className="mt-9 font-script text-4xl font-semibold text-white/90">Happier bedtimes. Brighter tomorrows.</p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Mobile hero */}
      <div className="relative bg-night lg:hidden" data-testid="hero-mobile">
        <Starfield count={26} />
        <div className="relative h-[140vw] overflow-hidden">
          <motion.img src="/assets/hero-mobile.png" alt="Little girl asleep cuddling a glowing teddy Bedtime Buddy under a starry night sky"
            initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, ease: EASE }}
            className="h-full w-full object-cover object-bottom" data-testid="hero-image-mobile" />
          <div className="absolute inset-0 bg-gradient-to-b from-night/70 via-night/5 to-night" aria-hidden="true" />
          <motion.img src="/assets/logo.png" alt="Bedtime Buddies" data-testid="hero-logo-mobile"
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
            className="absolute left-4 top-6 z-10 h-16 w-auto drop-shadow-xl" />
          <motion.img src="/assets/badge-smart-plushie.webp" alt="The Original Smart Plushie badge"
            initial={{ opacity: 0, scale: 0.6, rotate: -8 }} animate={{ opacity: 1, scale: 1, rotate: 6 }}
            transition={{ delay: 0.9, duration: 0.6, type: "spring", bounce: 0.45 }}
            className="absolute right-4 top-[40%] z-10 w-28 drop-shadow-xl"
            data-testid="hero-note-right" />
        </div>
        <div className="relative -mt-56 px-6 pb-16 text-center">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
            className="font-display text-[2rem] font-medium leading-[1.1] tracking-tight text-white drop-shadow-lg" data-testid="hero-headline-mobile">
            Bedtime feels different<br />when you're <span className="text-pink">not alone.</span> <Heart className="mb-1 inline h-7 w-7 text-pink" aria-hidden="true" />
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.3 }}
            className="mx-auto mt-4 max-w-sm text-[15px] leading-relaxed text-white/85" data-testid="hero-sub-mobile">
            Oversized, softly weighted companions that comfort, help with self-soothing, and bring a little more magic to bedtime.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.45 }}
            className="mt-8 flex flex-col items-center gap-4">
            <Link to="/buddy/luna-the-unicorn" data-testid="hero-find-buddy-cta"
              className="inline-flex w-full max-w-sm items-center justify-center gap-2 rounded-full bg-moon px-8 py-4 font-body text-base font-bold leading-none text-navy shadow-xl transition hover:scale-[1.03]">
              Find Their Buddy <ArrowRight size={17} />
            </Link>
            <button type="button" onClick={() => setVideoOpen(true)} data-testid="hero-watch-video-cta"
              className="inline-flex w-full max-w-sm items-center justify-center gap-2.5 rounded-full border border-white/50 px-8 py-4 font-body text-base font-semibold leading-none text-white transition hover:border-moon hover:text-moon">
              <Play size={16} className="fill-current" /> Watch the Video
            </button>
          </motion.div>
        </div>
      </div>
      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} />
    </section>
  );
}

const FEATURES = [
  { icon: Heart, bg: "#FBE7EF", fg: "#E56BB0", title: "Elongated cuddle-pillow body", sub: "Extra big hugs for bigger comfort." },
  { icon: Cloud, bg: "#EEE8FF", fg: "#8B76D9", title: "Softly weighted arms", sub: "Feels like a real hug." },
  { icon: Volume2, bg: "#DFF3FF", fg: "#4A9ECC", title: "Stories & soothing sounds", sub: "Calm minds. Happier bedtimes." },
  { icon: Lightbulb, bg: "#FFF3D1", fg: "#D9A52B", title: "Parent-controlled glow", sub: "Set the perfect nighttime mood." },
];

export default function Home() {
  const [buddies, setBuddies] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    api.get("/buddies", { params: { status: "launched" } }).then((r) => setBuddies(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const target = location.state?.scrollTo;
    if (!target) return;
    const t = setTimeout(() => {
      document.querySelector(`[data-testid="${target}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.replaceState({}, document.title);
    }, 500);
    return () => clearTimeout(t);
  }, [location.state]);


  return (
    <div data-testid="home-page" className="bg-cream">
      <Hero />

      {/* Feature bar */}
      <div className="relative z-10 mx-auto max-w-7xl lg:-mt-16 lg:px-6" data-testid="feature-bar">
        <Reveal y={24}>
          <div className="grid grid-cols-2 gap-4 rounded-t-[2.25rem] bg-white px-5 pb-8 pt-9 lg:rounded-[2.5rem] lg:gap-x-10 lg:gap-y-14 lg:px-12 lg:py-20 lg:shadow-[0_20px_60px_rgba(16,42,86,0.10)] xl:grid-cols-4 xl:gap-x-8">
            {FEATURES.map((f) => (
              <div key={f.title} className="flex flex-col items-center gap-3 rounded-[1.75rem] bg-cream px-4 py-7 text-center lg:gap-4 lg:rounded-none lg:bg-transparent lg:p-0 lg:shadow-none">
                <span className="flex h-16 w-16 items-center justify-center rounded-full lg:h-20 lg:w-20" style={{ background: f.bg, color: f.fg }}>
                  <f.icon size={34} />
                </span>
                <p className="font-display text-lg font-semibold leading-snug text-navy xl:whitespace-nowrap">{f.title}</p>
                <p className="font-script text-xl font-semibold leading-tight xl:whitespace-nowrap" style={{ color: f.fg }}>{f.sub}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>

      <FindBuddySection buddies={buddies} />

      <HomeStorySections />

      {/* Reviews placeholder */}
      <section className="bg-white px-6 py-20" data-testid="reviews-section">
        <div className="mx-auto max-w-6xl">
          <Reveal className="text-center">
            <h2 className="font-display text-3xl font-medium text-navy sm:text-4xl">Loved by little dreamers (and their parents).</h2>
            <p className="mt-3 text-xs font-bold uppercase tracking-[0.25em] text-navy/40">Customer reviews coming soon</p>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-[2rem] border border-dashed border-blush bg-cream p-8" data-testid={`review-placeholder-${i}`}>
                <div className="flex gap-1 text-moon/70">{[...Array(5)].map((_, s) => <Star key={s} size={15} fill="currentColor" />)}</div>
                <div className="mt-5 h-3 w-3/4 rounded-full bg-blush" />
                <div className="mt-3 h-3 w-full rounded-full bg-blush" />
                <div className="mt-3 h-3 w-2/3 rounded-full bg-blush" />
                <p className="mt-6 text-[11px] font-bold uppercase tracking-widest text-navy/30">Customer review coming soon</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* UGC */}
      {/* Final emotional CTA — the one dark section */}
      <section className="relative overflow-hidden bg-navy" data-testid="final-cta-section">
        <img src="/assets/final-cta.webp" alt="Child peacefully asleep cuddling a softly glowing teddy bear" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-night/80 via-navy/40 to-transparent" aria-hidden="true" />
        <div className="relative mx-auto flex min-h-[70vh] max-w-7xl items-center px-6 py-24">
          <Reveal className="max-w-2xl">
            <h2 className="font-display text-4xl font-medium leading-[1.02] text-white drop-shadow-lg sm:text-5xl lg:text-6xl">
              Small dreams make a brighter world
            </h2>
            <p className="mt-5 font-display text-xl font-medium text-moon sm:text-2xl">
              Better sleep. Brighter tomorrows. With Bedtime Buddies
            </p>
            <Link to="/buddy/luna-the-unicorn" data-testid="final-cta-btn"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-pink px-10 py-5 font-body text-lg font-bold leading-none text-navy shadow-xl transition hover:scale-[1.05] hover:bg-moon">
              Find Their Buddy <ArrowRight size={20} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
